import type { AnalyticsConfig, ConversionKey } from "./types";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __nsAnalytics?: { config: AnalyticsConfig; loaded: boolean };
    openCookieSettings?: () => void;
  }
}

export const CONSENT_STORAGE_KEY = "ns-cookie-consent-v1";

export type ConsentState = {
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
};

export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (typeof parsed?.analytics !== "boolean") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(state: Omit<ConsentState, "decidedAt">): ConsentState {
  const next: ConsentState = { ...state, decidedAt: new Date().toISOString() };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable */
  }
  return next;
}

export function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/** Consent Mode v2: deny everything until the visitor decides. */
export function initConsentDefaults() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer!.push(args);
    };
  }
  const stored = readConsent();
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  if (stored) applyConsent(stored);
}

export function applyConsent(state: Pick<ConsentState, "analytics" | "marketing">) {
  gtag("consent", "update", {
    analytics_storage: state.analytics ? "granted" : "denied",
    ad_storage: state.marketing ? "granted" : "denied",
    ad_user_data: state.marketing ? "granted" : "denied",
    ad_personalization: state.marketing ? "granted" : "denied",
  });
}

let scriptLoaded = false;

/** Runs fn once the page is fully loaded and the main thread is idle. */
function whenIdle(fn: () => void) {
  const schedule = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(fn, { timeout: 3000 });
    } else {
      window.setTimeout(fn, 1500);
    }
  };
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}

/**
 * Loads gtag.js once, after the page has loaded and the browser is idle.
 * All gtag() calls made beforehand are queued in dataLayer and processed as
 * soon as the script lands, so no events are lost, but GA/GTM never compete
 * with first paint, the LCP image or hydration.
 */
export function loadGtag(config: AnalyticsConfig) {
  if (typeof window === "undefined" || scriptLoaded) return;
  if (!config.enabled) return;
  const primaryId = config.ga4_measurement_id || config.ads_conversion_id;
  if (!primaryId) return;

  scriptLoaded = true;
  window.__nsAnalytics = { config, loaded: true };

  gtag("js", new Date());
  if (config.ga4_measurement_id) {
    gtag("config", config.ga4_measurement_id, {
      send_page_view: false,
      debug_mode: config.debug_mode || undefined,
    });
  }
  if (config.ads_conversion_id) {
    gtag("config", config.ads_conversion_id, {
      allow_enhanced_conversions: config.enhanced_conversions,
      send_page_view: false,
    });
  }

  whenIdle(() => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`;
    document.head.appendChild(script);
  });
}

function baseParams(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  const url = new URL(window.location.href);
  const utm: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"]) {
    const v = url.searchParams.get(key);
    if (v) utm[key] = v;
  }
  const width = window.innerWidth;
  return {
    page_location: window.location.href,
    page_path: url.pathname,
    page_title: document.title,
    device_type: width < 768 ? "mobile" : width < 1200 ? "tablet" : "desktop",
    traffic_source: utm.utm_source ?? (document.referrer ? new URL(document.referrer).hostname : "direct"),
    timestamp: new Date().toISOString(),
    ...utm,
  };
}

export function trackPageView(path?: string) {
  const cfg = window.__nsAnalytics?.config;
  if (!cfg?.ga4_measurement_id) return;
  gtag("event", "page_view", {
    ...baseParams(),
    ...(path ? { page_path: path } : {}),
    send_to: cfg.ga4_measurement_id,
  });
}

export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  gtag("event", name, { ...baseParams(), ...params });
}

export type ConversionPayload = Record<string, unknown> & {
  value?: number;
  currency?: string;
  transaction_id?: string;
  /** Enhanced conversions user data. */
  email?: string;
  phone?: string;
  name?: string;
};

/**
 * Sends a GA4 event and, when a Google Ads label is configured, the matching
 * Ads conversion, including enhanced conversion user data where allowed.
 */
export function trackConversion(key: ConversionKey, eventName: string, payload: ConversionPayload = {}) {
  if (typeof window === "undefined") return;
  const cfg = window.__nsAnalytics?.config;
  const { email, phone, name, ...rest } = payload;

  trackEvent(eventName, rest);

  if (!cfg?.ads_conversion_id) return;
  const label = cfg.conversion_labels?.[key];
  if (!label) return;

  if (cfg.enhanced_conversions && (email || phone || name)) {
    const parts = (name ?? "").trim().split(/\s+/);
    gtag("set", "user_data", {
      ...(email ? { email: email.trim().toLowerCase() } : {}),
      ...(phone ? { phone_number: normalisePhone(phone) } : {}),
      ...(parts[0]
        ? { address: { first_name: parts[0], last_name: parts.slice(1).join(" ") || undefined } }
        : {}),
    });
  }

  gtag("event", "conversion", {
    send_to: `${cfg.ads_conversion_id}/${label}`,
    ...(rest.value !== undefined ? { value: rest.value } : {}),
    currency: (rest.currency as string) ?? "ZAR",
    ...(rest.transaction_id ? { transaction_id: rest.transaction_id } : {}),
  });
}

function normalisePhone(raw: string) {
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("27")) return `+${digits}`;
  if (digits.startsWith("0")) return `+27${digits.slice(1)}`;
  return `+${digits}`;
}
